import { cn } from "@/lib/utils";
import type { Message, StreamingWord } from "@/lib/ChatModel";
import { RenderMarkdown } from "./ChatHelpers";
import { RefreshCcw, Copy, Share2, ThumbsUp, ThumbsDown } from "lucide-react";
import { Skeleton } from "../ui/skeleton";

/**
 * @param props for rendering message
 * @param streamingWords is a global streaming words array, and not scoped to any particular message.
 * This is pretty hacky and might cause issues, but right now it's a simple way to avoid re-renders
 * Eventually it needs to be fixed
 */
export function RenderMessage(
  props: Message & { streamingWords?: StreamingWord[] },
) {
  const messageType = props.type || (props.role === "user" ? "user" : "system");

  return (
    <div
      key={props.id}
      data-testid="message-wrapper"
      className={cn(
        "flex flex-col",
        messageType === "user" ? "items-end" : "items-start",
      )}
    >
      <div
        data-testid="message-container"
        className={cn(
          "max-w-[80%] px-4 py-2 rounded-2xl",
          messageType === "user"
            ? "border border-gray-200 rounded-br-none text-primary"
            : "text-primary",
        )}
      >
        {/* For completed messages or user messages */}
        {(props.completed || messageType === "user") && (
          <div data-testid="message-content" className="whitespace-pre-wrap">
            <RenderMarkdown content={props.content} />
          </div>
        )}

        {/* For streaming system messages */}
        {messageType === "system" &&
          !props.completed &&
          (props.streamingWords?.length ? (
            <div data-testid="streaming-message">
              <div
                className="inline whitespace-pre-wrap"
                data-testid="streaming-content"
              >
                {props.streamingWords.map((word) => (
                  <span key={word.id} className="animate-text-fade-in inline">
                    {word.text}
                  </span>
                ))}
              </div>
              <TextLoadingSkeleton />
            </div>
          ) : (
            <span data-testid="thinking-indicator" className="inline italic">
              Thinking...
            </span>
          ))}
      </div>

      {/* Message actions */}
      {messageType === "system" && props.completed && (
        <div className="flex items-center gap-2 px-4 mt-1 mb-2">
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ThumbsUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ThumbsDown className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function TextLoadingSkeleton() {
  return (
    <>
      <div className="animate-pulse flex gap-2 mt-2">
        <Skeleton className="w-10 h-4" />
        <Skeleton className="w-48 h-4" />
        <Skeleton className="w-8 h-4" />
      </div>
      <div className="animate-pulse flex gap-2 mt-2">
        <Skeleton className="w-20 h-4" />
        <Skeleton className="w-8 h-4" />
        <Skeleton className="w-48 h-4" />
      </div>
      <div className="animate-pulse flex gap-2 mt-2">
        <Skeleton className="w-52 h-4" />
        <Skeleton className="w-8 h-4" />
        <Skeleton className="w-40 h-4" />
      </div>
    </>
  );
}
